// Route Configuration - Single Source of Truth
// This file contains all route definitions and role-based access configurations

// Define all available routes with their metadata
export const ROUTES = {
  // Public routes
  LOGIN: { path: '/', name: 'Login', isPublic: true },
  BECOME_A_CUSTOMER: { path: '/become-a-customer', name: 'Become a Customer', isPublic: true },

  // Authentication routes
  AUTH_SUCCESS: { path: '/auth/success', name: 'Auth Success', isPublic: true },
  AUTH_ERROR: { path: '/auth/error', name: 'Auth Error', isPublic: true },
  SUCCESS: { path: '/success', name: 'Success', isPublic: true }, // SAML success

  // Protected routes
  HOME: { path: '/home', name: 'Home', icon: 'HOME' },
  CHANGE_PASSWORD: { path: '/change-password', name: 'Change Password' },
  USER_PROFILE: { path: '/user-profile', name: 'User Profile' },

  // Onboarding routes (authenticated but no header/sidebar)
  ONBOARDING_DOCUMENT_SIGNING: { path: '/onboarding/document-signing', name: 'Document Signing', isOnboarding: true },
  ONBOARDING_CUSTOMER: { path: '/onboarding/customer-details', name: 'Customer Details', isOnboarding: true },
  ONBOARDING_ADMIN: { path: '/onboarding/admin-details', name: 'Admin Details', isOnboarding: true },
  ONBOARDING_FACILITY: { path: '/onboarding/facility-details', name: 'Facility Details', isOnboarding: true },
  ONBOARDING_PROVIDER: { path: '/onboarding/provider-details', name: 'Provider Details', isOnboarding: true },

  // User accessible routes
  // TEMPORARY: Old Call History menu commented out
  // CALL_HISTORY: { path: '/call-history', name: 'Call History', icon: 'History' },
  CALL_HISTORY: { path: '/call-history', name: 'Call History', icon: 'History' }, // Was V2, now renamed to Call History
  QUESTION_MANAGEMENT: { path: '/question-management', name: 'Question Management', icon: 'question-management-icon' },
  FAX_AGENT: { path: '/fax-agent', name: 'Fax Agent', icon: 'FaxAgent' },
  FAX_AGENT_CREATE_TEMPLATE: { path: '/fax-agent/create-template', name: 'Fax Agent' },
  FAX_AGENT_RECEIVED_FAX: { path: '/fax-agent/received-fax/:faxId', name: 'Fax Agent' },
  FAX_AGENT_BUY_FAX_NUMBER: { path: '/fax-agent/buy-fax-number', name: 'Fax Agent' },

  // Supervisor accessible routes
  USER_MANAGEMENT: { path: '/user-management', name: 'User Management', icon: 'user-management' },
  CALL_AUTOMATION: { path: '/call-automation', name: 'Call Automation', icon: 'CALL-AUTOMATION' },
  CALL_AUTOMATION_V2: { path: '/call-automation-v2', name: 'Call Automation V2' },
  PROVIDERS: { path: '/providers', name: 'Providers', icon: 'Provider' },

  // Admin accessible routes
  ADVANCED_ANALYTICS: { path: '/advancedAnalytics', name: 'Advanced Analytics' },
  BILLING: { path: '/billing', name: 'Billing', icon: 'Payment' },
  FACILITY: { path: '/facility', name: 'Facility', icon: 'Facility' },
  EHR_MANAGEMENT: { path: '/ehr-management', name: 'EHR Management', icon: 'ehr' },
  DEPARTMENTS: { path: '/departments', name: 'Departments', icon: 'department' },
  DEPARTMENT_DETAILS: { path: '/departments/:id', name: 'Department Details' },
  SSO_SETUP: { path: '/sso-setup', name: 'SSO Settings', icon: 'SSO' },
  DOCUMENT_SIGNING: { path: '/document-signing', name: 'Document Signing', icon: 'documentation' },

  // Special routes
  UNAUTHORIZED: { path: '/403', name: 'Unauthorized' },

  // Documentation routes
  API_DOCS: { path: '/api-docs', name: 'API Documentation', icon: 'documentation' }
};

// Role hierarchy and access configuration
export const ROLE_HIERARCHY = {
  // Common access for all authenticated users
  COMMON: [
    ROUTES.HOME.name,
    ROUTES.CHANGE_PASSWORD.name,
    ROUTES.USER_PROFILE.name,
    ROUTES.AUTH_SUCCESS.path,
    ROUTES.AUTH_ERROR.path,
    ROUTES.BECOME_A_CUSTOMER.path
  ],

  // Basic user access
  USER_SPECIFIC: [
    ROUTES.CALL_HISTORY.name,
    // ROUTES.CALL_HISTORY_V2.name, // Removed - now using single Call History
    ROUTES.CALL_AUTOMATION.name,
    ROUTES.DOCUMENT_SIGNING.name,
    ROUTES.QUESTION_MANAGEMENT.name,
    ROUTES.FAX_AGENT.name,
    ROUTES.FAX_AGENT_CREATE_TEMPLATE.name,
    ROUTES.FAX_AGENT_RECEIVED_FAX.name,
    ROUTES.FAX_AGENT_BUY_FAX_NUMBER.name,
    ROUTES.API_DOCS.name
  ],

  // Supervisor additional access
  SUPERVISOR_SPECIFIC: [
    ROUTES.USER_MANAGEMENT.name,
    ROUTES.CALL_AUTOMATION.name,
    ROUTES.FACILITY.name,
    ROUTES.EHR_MANAGEMENT.name,
    ROUTES.DEPARTMENTS.name,
    ROUTES.PROVIDERS.name,
    ROUTES.DOCUMENT_SIGNING.name
  ],

  // Admin additional access
  ADMIN_SPECIFIC: [
    ROUTES.BILLING.name,
    ROUTES.FACILITY.name,
    ROUTES.EHR_MANAGEMENT.name,
    ROUTES.DEPARTMENTS.name,
    ROUTES.SSO_SETUP.name,
    ROUTES.DOCUMENT_SIGNING.name
  ]
};

// Get all valid routes for route validation
export const getValidRoutes = () => {
  return Object.values(ROUTES).map(route => route.path);
};

// Get menu access configuration for roles
export const getMenuAccessForRoles = () => {
  const { COMMON, USER_SPECIFIC, SUPERVISOR_SPECIFIC, ADMIN_SPECIFIC } = ROLE_HIERARCHY;

  // Basic user access (common + user specific)
  const userAccess = [...COMMON, ...USER_SPECIFIC];

  // Supervisor access (user access + supervisor specific)
  const supervisorAccess = [...userAccess, ...SUPERVISOR_SPECIFIC];

  // Admin access (supervisor access + admin specific)
  const adminAccess = [...supervisorAccess, ...ADMIN_SPECIFIC];

  return {
    admin: adminAccess,
    supervisor: supervisorAccess,
    user: userAccess
  };
};

// List of customer IDs allowed to access API Documentation
// Add customer IDs here to grant access
export const ALLOWED_API_DOCS_CUSTOMER_IDS = [
  // Add customer IDs here, e.g.:
  "683171f20d5a826f4640bf71",
  "685e41c1833efb449990f81a",
  "687617b80ddacd0feec512bd",
  "6a1efb13722b19e966cce592",
];

/**
 * Checks if the current customer has access to API Documentation
 * @param {string} customerId - The current customer ID
 * @returns {boolean} True if customer has access, false otherwise
 */
export const hasApiDocsAccess = (customerId) => {
  if (!customerId || ALLOWED_API_DOCS_CUSTOMER_IDS.length === 0) {
    return false;
  }
  return ALLOWED_API_DOCS_CUSTOMER_IDS.includes(customerId);
};

// Get menu items for sidebar (routes that should appear in navigation)
export const getMenuItems = (customerId = null) => {
  // Define the desired order for menu items
  const menuOrder = [
    'Home',
    'Facility',
    'EHR Management',
    'Departments',
    'User Management',
    'Providers',
    'Call Automation',
    'Call History',
    // 'Call History V2', // Removed - merged into Call History
    'Question Management',
    'Fax Agent',
    'Advanced Analytics',
    'SSO Settings',
    'API Documentation',
    'Billing',
  ];

  // Get all routes with icons
  const allMenuRoutes = Object.values(ROUTES).filter(route => route.icon);

  // Sort them according to the defined order
  let sortedMenus = menuOrder
    .map(menuName => allMenuRoutes.find(route => route.name === menuName))
    .filter(Boolean); // Remove any undefined items

  // Filter out API Documentation if customer doesn't have access
  if (customerId !== null) {
    sortedMenus = sortedMenus.filter(route => {
      if (route.name === 'API Documentation') {
        return hasApiDocsAccess(customerId);
      }
      return true;
    });
  }

  return sortedMenus.map(route => ({
    name: route.name,
    path: route.path,
    icon: route.icon
  }));
};

// Utility function to get route by path
export const getRouteByPath = (path) => {
  return Object.values(ROUTES).find(route => route.path === path);
};

// Utility function to check if route is public
export const isPublicRoute = (path) => {
  const route = getRouteByPath(path);
  return route?.isPublic || false;
};

// Utility function to check if route is onboarding
export const isOnboardingRoute = (path) => {
  const route = getRouteByPath(path);
  return route?.isOnboarding || false;
}; 
